import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const fmt  = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
const fmtPct = n => (Number(n || 0)).toFixed(1) + '%'

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export default function BudgetSummary({ projectId: initProjectId }) {
  const [projects,       setProjects]       = useState([])
  const [selectedProject,setSelectedProject]= useState(null)
  const [loading,        setLoading]        = useState(false)
  const [data,           setData]           = useState(null)

  // Load projects
  useEffect(() => {
    supabase.from('projects')
      .select('id, name, contract_value, currency, organizations(name)')
      .order('name')
      .then(({ data }) => setProjects(data || []))
  }, [])

  // Auto-select from prop
  useEffect(() => {
    if (initProjectId && projects.length > 0) {
      const p = projects.find(x => x.id === initProjectId)
      if (p) { setSelectedProject(p); loadSummary(p) }
    }
  }, [initProjectId, projects])

  const loadSummary = useCallback(async (proj) => {
    const pid = proj?.id || selectedProject?.id
    if (!pid) return
    setLoading(true)

    const [directRes, indirectRes, boqRes] = await Promise.all([
      supabase.from('tender_direct_items')
        .select('description, package_name, is_package_header, total_budget, sort_order')
        .eq('project_id', pid)
        .order('sort_order', { ascending: true }),
      supabase.from('tender_indirect_items')
        .select('description, department, is_package_header, tender_budget, sort_order')
        .eq('project_id', pid)
        .order('sort_order', { ascending: true }),
      supabase.from('boq_items')
        .select('total_selling')
        .eq('project_id', pid),
    ])

    // Direct: group activities by package_name, sum total_budget
    const directPkgs = {}
    for (const r of (directRes.data || [])) {
      if (r.is_package_header) continue
      const pkg = (r.package_name || r.description || 'Other').trim()
      if (!directPkgs[pkg]) directPkgs[pkg] = { budget: 0 }
      directPkgs[pkg].budget += parseFloat(r.total_budget) || 0
    }

    // Indirect: group packages by description (is_package_header = true), use tender_budget
    const indirectPkgs = {}
    for (const r of (indirectRes.data || [])) {
      if (!r.is_package_header) continue
      const pkg = (r.description || 'Other').trim()
      if (!indirectPkgs[pkg]) indirectPkgs[pkg] = { budget: 0 }
      indirectPkgs[pkg].budget += parseFloat(r.tender_budget) || 0
    }

    const totalDirect   = Object.values(directPkgs).reduce((s, p) => s + p.budget, 0)
    const totalIndirect = Object.values(indirectPkgs).reduce((s, p) => s + p.budget, 0)
    const grandTotal    = totalDirect + totalIndirect
    const totalBOQ      = (boqRes.data || []).reduce((s, r) => s + (parseFloat(r.total_selling) || 0), 0)
    const cv            = parseFloat((proj || selectedProject)?.contract_value) || 0

    setData({ directPkgs, indirectPkgs, totalDirect, totalIndirect, grandTotal, totalBOQ, cv,
              currency: (proj || selectedProject)?.currency || 'SAR' })
    setLoading(false)
  }, [selectedProject])

  function handleSelect(p) {
    setSelectedProject(p)
    setData(null)
    loadSummary(p)
  }

  // ── Project picker ──────────────────────────────────────
  if (!selectedProject) return (
    <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>
      <div className="mb-6">
        <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">Budget Summary</h1>
        <p className="text-[#6B7280] text-sm mt-1">Select a project to view the full budget summary</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map(p => (
          <button key={p.id} onClick={() => handleSelect(p)}
            className="text-left bg-white border border-[#E5E7EB] rounded-xl p-4 hover:border-[#1A7A4A] hover:shadow-sm transition-all">
            <p className="font-semibold text-[#0F1017]">{p.name}</p>
            <p className="text-xs text-[#9CA3AF] mt-0.5">{p.organizations?.name}</p>
            <p className="text-sm font-medium text-[#1A7A4A] mt-1">{fmt(p.contract_value)} {p.currency}</p>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">Budget Summary</h1>
          <button onClick={() => setSelectedProject(null)}
            className="text-xs text-[#9CA3AF] hover:text-[#6B7280] border border-[#E5E7EB] rounded px-2 py-0.5">
            {selectedProject.name} ×
          </button>
        </div>
        <button onClick={() => loadSummary(selectedProject)} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#1A7A4A] text-white text-sm rounded-lg hover:bg-[#0F1017] disabled:opacity-50 transition-colors">
          <RefreshIcon />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {loading && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 0', flexDirection:'column', gap:12 }}>
          <div style={{ width:28, height:28, border:'3px solid #D1D5DB', borderTopColor:'#1A7A4A', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
          <p style={{ fontSize:13, color:'#9CA3AF' }}>Loading...</p>
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── Top KPI Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Budget — hero card */}
            <div className="col-span-2 lg:col-span-4 bg-[#0F1017] rounded-xl p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#9CA3AF] mb-1">Total Budget (Direct + Indirect)</p>
                <p className="text-3xl font-bold text-white">{fmt(data.grandTotal)}</p>
                <p className="text-xs text-[#6B7280] mt-1">{data.currency} · Direct {fmt(data.totalDirect)} + Indirect {fmt(data.totalIndirect)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#6B7280] uppercase tracking-wide mb-1">vs Contract</p>
                <p className={`text-xl font-bold ${data.grandTotal > data.cv ? 'text-[#C43927]' : 'text-[#1A7A4A]'}`}>
                  {data.cv > 0 ? ((data.grandTotal / data.cv) * 100).toFixed(1) + '%' : '—'}
                </p>
                <p className="text-xs text-[#6B7280] mt-0.5">of {fmt(data.cv)} {data.currency}</p>
              </div>
            </div>
            {[
              { label: 'Contract Value', value: data.cv,            color: 'text-[#2E2C29]',   bg: 'bg-white',        border: 'border-[#E5E7EB]',   icon: '🏗' },
              { label: 'BOQ Total',      value: data.totalBOQ,      color: 'text-[#1A7A4A]',    bg: 'bg-[#F3F4F6]',      border: 'border-[#1A7A4A]/30',    icon: '📋' },
              { label: 'Direct Budget',  value: data.totalDirect,   color: 'text-[#1A7A4A]',  bg: 'bg-[#F3F4F6]',    border: 'border-[#E5E7EB]',  icon: null },
              { label: 'Indirect Budget',value: data.totalIndirect, color: 'text-[#2E2C29]',  bg: 'bg-[#F3F4F6]',    border: 'border-[#E5E7EB]',  icon: null },
            ].map(c => (
              <div key={c.label} className={`${c.bg} border ${c.border} rounded-xl p-5`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">{c.label}</p>
                  <span className="text-lg">{c.icon}</span>
                </div>
                <p className={`text-2xl font-bold ${c.color}`}>{fmt(c.value)}</p>
                <p className="text-xs text-[#9CA3AF] mt-1">{data.currency}</p>
              </div>
            ))}
          </div>

          {/* ── Grand Total + Variance ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Grand Total */}
            <div className="bg-[#0F1017] rounded-xl px-6 py-5 flex items-center justify-between">
              <div>
                <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wide mb-1">Grand Total Budget</p>
                <p className="text-white text-3xl font-bold">{fmt(data.grandTotal)}</p>
                <p className="text-[#9CA3AF] text-xs mt-1">Direct + Indirect</p>
              </div>
              <div className="text-right space-y-1">
                <p className="text-[#9CA3AF] text-xs">Direct: <span className="font-semibold text-white">{fmt(data.totalDirect)}</span></p>
                <p className="text-[#9CA3AF] text-xs">Indirect: <span className="font-semibold text-white">{fmt(data.totalIndirect)}</span></p>
                <p className="text-[#9CA3AF] text-xs mt-2">{data.currency}</p>
              </div>
            </div>

            {/* Variance vs Contract */}
            {data.cv > 0 && (() => {
              const variance  = data.cv - data.grandTotal
              const budgetPct = (data.grandTotal / data.cv * 100)
              const ok        = variance >= 0
              return (
                <div className={`rounded-xl border px-6 py-5 ${ok ? 'bg-[#F3F4F6] border-[#1A7A4A/30]' : 'bg-[#F3F4F6] border-[#C43927/30]'}`}>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Budget vs Contract Value</p>
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <p className={`text-3xl font-bold ${ok ? 'text-[#1A7A4A]' : 'text-[#C43927]'}`}>{budgetPct.toFixed(1)}%</p>
                      <p className="text-xs text-[#6B7280] mt-0.5">of contract value</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${ok ? 'text-[#1A7A4A]' : 'text-[#C43927]'}`}>
                        {ok ? '✓ Within budget' : '⚠️ Over budget'}
                      </p>
                      <p className="text-xs text-[#6B7280] mt-0.5">
                        Variance: {ok ? '+' : '-'}{fmt(Math.abs(variance))} {data.currency}
                      </p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden border border-[#E5E7EB]">
                    <div
                      className={`h-full rounded-full transition-all ${ok ? 'bg-[#F3F4F6]0' : 'bg-[#F3F4F6]0'}`}
                      style={{ width: `${Math.min(budgetPct, 100)}%` }}
                    />
                  </div>
                </div>
              )
            })()}
          </div>

          {/* ── Package Breakdown ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* Direct Packages */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="bg-[#2E2C29] px-4 py-3 flex items-center justify-between">
                <span className="text-white font-bold text-sm">💰 Direct Budget Packages</span>
                <span className="text-[#9CA3AF] text-xs font-semibold bg-[#2E2C29] px-2 py-0.5 rounded">
                  {fmt(data.totalDirect)} {data.currency}
                </span>
              </div>

              {Object.keys(data.directPkgs).length === 0 ? (
                <div className="py-10 text-center text-[#9CA3AF] text-sm">لا يوجد direct budget</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">Package</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">Budget</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#9CA3AF] uppercase w-16">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.directPkgs)
                      .sort((a, b) => b[1].budget - a[1].budget)
                      .map(([pkg, d], i) => (
                        <tr key={pkg} className={`border-b border-[#F3F4F6] hover:bg-[#FAFAFA] ${i % 2 === 0 ? '' : 'bg-[#F3F4F6]/40'}`}>
                          <td className="px-4 py-2.5 text-[#2E2C29] font-medium text-sm">{pkg}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-[#1A7A4A]">{fmt(d.budget)}</td>
                          <td className="px-4 py-2.5 text-right text-[#9CA3AF] text-xs">
                            {data.totalDirect > 0 ? fmtPct(d.budget / data.totalDirect * 100) : '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot className="border-t-2 border-[#E5E7EB] bg-[#F3F4F6]">
                    <tr>
                      <td className="px-4 py-2.5 font-bold text-[#0F1017] text-xs uppercase">Total Direct</td>
                      <td className="px-4 py-2.5 text-right font-bold text-[#0F1017]">{fmt(data.totalDirect)}</td>
                      <td className="px-4 py-2.5 text-right text-[#1A7A4A]/70 text-xs font-semibold">
                        {data.grandTotal > 0 ? fmtPct(data.totalDirect / data.grandTotal * 100) : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {/* Indirect Packages */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="bg-[#2E2C29] px-4 py-3 flex items-center justify-between">
                <span className="text-white font-bold text-sm">📦 Indirect Budget Packages</span>
                <span className="text-[#9CA3AF] text-xs font-semibold bg-[#0F1017] px-2 py-0.5 rounded">
                  {fmt(data.totalIndirect)} {data.currency}
                </span>
              </div>

              {Object.keys(data.indirectPkgs).length === 0 ? (
                <div className="py-10 text-center text-[#9CA3AF] text-sm">لا يوجد indirect budget</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">Package</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">Budget</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#9CA3AF] uppercase w-16">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.indirectPkgs)
                      .sort((a, b) => b[1].budget - a[1].budget)
                      .map(([pkg, d], i) => (
                        <tr key={pkg} className={`border-b border-[#F3F4F6] hover:bg-[#FAFAFA] ${i % 2 === 0 ? '' : 'bg-[#F3F4F6]/40'}`}>
                          <td className="px-4 py-2.5 text-[#2E2C29] font-medium text-sm">{pkg}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-[#2E2C29]">{fmt(d.budget)}</td>
                          <td className="px-4 py-2.5 text-right text-[#9CA3AF] text-xs">
                            {data.totalIndirect > 0 ? fmtPct(d.budget / data.totalIndirect * 100) : '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot className="border-t-2 border-[#E5E7EB] bg-[#F3F4F6]">
                    <tr>
                      <td className="px-4 py-2.5 font-bold text-[#0F1017] text-xs uppercase">Total Indirect</td>
                      <td className="px-4 py-2.5 text-right font-bold text-[#0F1017]">{fmt(data.totalIndirect)}</td>
                      <td className="px-4 py-2.5 text-right text-[#6B7280] text-xs font-semibold">
                        {data.grandTotal > 0 ? fmtPct(data.totalIndirect / data.grandTotal * 100) : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>

          {/* ── BOQ vs Budget Comparison ── */}
          {data.totalBOQ > 0 && (
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="bg-[#2E2C29] px-4 py-3">
                <span className="text-white font-bold text-sm">📊 Budget Comparison</span>
              </div>
              <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {[
                  { label: 'BOQ (Selling)',    value: data.totalBOQ,      color: 'text-[#1A7A4A]',   note: 'Contract revenue target' },
                  { label: 'Direct Budget',    value: data.totalDirect,   color: 'text-[#1A7A4A]', note: fmtPct(data.totalBOQ > 0 ? data.totalDirect / data.totalBOQ * 100 : 0) + ' of BOQ' },
                  { label: 'Indirect Budget',  value: data.totalIndirect, color: 'text-[#2E2C29]', note: fmtPct(data.totalBOQ > 0 ? data.totalIndirect / data.totalBOQ * 100 : 0) + ' of BOQ' },
                  { label: 'Total Cost Budget',value: data.grandTotal,    color: 'text-[#1A7A4A]',note: fmtPct(data.totalBOQ > 0 ? data.grandTotal / data.totalBOQ * 100 : 0) + ' of BOQ' },
                ].map(c => (
                  <div key={c.label} className="border border-[#F3F4F6] rounded-lg p-3">
                    <p className="text-xs text-[#9CA3AF] mb-1">{c.label}</p>
                    <p className={`text-lg font-bold ${c.color}`}>{fmt(c.value)}</p>
                    <p className="text-xs text-[#9CA3AF] mt-0.5">{c.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </>
      )}

      {!loading && !data && (
        <div className="text-center py-20 bg-[#F3F4F6] rounded-xl border border-[#E5E7EB]">
          <div className="text-5xl mb-3">📈</div>
          <p className="text-[#6B7280]">Click Refresh to load the summary</p>
        </div>
      )}
    </div>
  )
}
